import React from 'react';
import { useTranslation } from 'react-i18next';
import { Droplet, TrendingUp, Award, BarChart2 } from 'lucide-react';

interface WaterTrackingDashboardProps {
  dailyGoal: number;
  waterAmount: number;
  streak: number;
  points: number;
  totalWaterConsumed: number;
  onAddWater: () => void;
}

const WaterTrackingDashboard: React.FC<WaterTrackingDashboardProps> = ({
  dailyGoal,
  waterAmount,
  streak,
  points,
  totalWaterConsumed,
  onAddWater
}) => {
  const { t } = useTranslation();
  
  const progressPercentage = Math.min((waterAmount / dailyGoal) * 100, 100);
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progressPercentage / 100) * circumference;
  const isLimitReached = waterAmount >= dailyGoal;
  const formattedTotalWater = (totalWaterConsumed / 1000).toFixed(2);

  return (
    <div className="flex flex-col items-center">
      {/* Progress visualization */}
      <div className="relative w-full max-w-xs mx-auto mb-8">
        <div className="aspect-square relative">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="10"
              className="opacity-25"
            />
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={isLimitReached ? "#10B981" : "#3B82F6"}
              strokeWidth="10"
              strokeLinecap="round"
              className="transition-all duration-1000 ease-in-out"
              style={{
                strokeDasharray: circumference,
                strokeDashoffset: offset
              }}
            />
          </svg>

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <Droplet className={`h-12 w-12 ${isLimitReached ? 'text-green-600' : 'text-blue-600'} mb-2`} />
            <div className="text-3xl font-bold text-gray-900">
              {waterAmount}
              <span className="text-lg font-normal text-gray-500 ml-1">ml</span>
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('dailyGoal')}: {dailyGoal}ml
            </div>
          </div>
        </div>
      </div>

      {/* Add water button */}
      <button
        onClick={onAddWater}
        disabled={isLimitReached}
        className={`w-full max-w-md py-4 px-6 rounded-xl font-medium transition-all duration-200 flex items-center justify-center mx-auto ${
          isLimitReached
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transform hover:-translate-y-0.5'
        }`}
      >
        <Droplet className="h-5 w-5 mr-2" />
        {isLimitReached ? t('limitReached') : t('validateConsumption')}
      </button>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4 w-full max-w-md mb-6 mt-8">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">{t('streak')}</span>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-blue-900">{streak}</div>
          <div className="text-xs text-blue-700 mt-1">{t('daysInARow')}</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-900">{t('points')}</span>
            <Award className="h-4 w-4 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-purple-900">{points}</div>
          <div className="text-xs text-purple-700 mt-1">{t('totalEarned')}</div>
        </div>
      </div>

      {/* Total water consumed stat */}
      <div className="w-full max-w-md mb-6 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-cyan-900">{t('totalWaterConsumed')}</span>
          <BarChart2 className="h-4 w-4 text-cyan-600" />
        </div>
        <div className="text-2xl font-bold text-cyan-900">{formattedTotalWater}L</div>
      </div>

      {/* Limit reached message */}
      {isLimitReached && (
        <div className="mt-4 text-sm text-yellow-600 text-center">
          {t('comeBackTomorrow')}
        </div>
      )}
    </div>
  );
};

export default WaterTrackingDashboard;
